import { PlatformLocation } from '@angular/common';
import { Injectable } from '@angular/core';

import { InstrumentationParam, InstrumentationService, KnownInstrumentationEvents } from '../instrumentation/instrumentation.service';
import { FetchStrategy, InstrumentedHttp, InstrumentedHttpDataResponse, InstrumentedHttpPromise } from '../instrumentation/instrumented-http.service';
import { HttpRequestOptions } from '../models/http-request-options.model';
import { GraphOneNotePage } from '../models/onenote-graph.model';
import { ClassNoteBook, OneNoteGetClassNoteBookResponse, OneNoteGetNoteBookSectionsResponse } from '../models/onenote.model';
import { ContextService } from '../shared-context/context.service';
import { AccessTokenConsumer, AccessTokenService } from './access-token.service';
import { OfficeHomeApiConfigService } from './officehome-api-config.service';
import { OneNoteConfigService } from './onenote-config.service';
import { SchedulerService } from './scheduler.service';
import { XhrService, XhrServiceError } from './xhr.service';

import { OneNoteSection } from '@office-start/create-onenote-page-dialog-control';

/**
 * Service that makes requests to OneNote apis.
 */
@Injectable({
    providedIn: 'root'
})
export class OneNoteService extends XhrService {
    public readonly COPY_CLASS_NOTEBOOK = '/notebooks/saveacopy';
    // Url calling to prod endpoint for client development mode
    private readonly CLIENT_DEV_MODE_RESOURCE_URL = 'https://www.onenote.com';
    private readonly BASE_URL: string = '/api';
    private readonly VERSION: string = '1.0';
    private readonly newPageHtml: string = `<!DOCTYPE html>
    <html>
      <head>
        <title>{PAGETITLE}</title>
      </head>
      <body>
      </body>
    </html>`;
    private getClassNoteBookPromise: Promise<Array<ClassNoteBook>> = null;
    protected inMockMode: boolean;

    constructor(
        accessTokenService: AccessTokenService,
        instrumentationService: InstrumentationService,
        instrumentedHttp: InstrumentedHttp,
        contextService: ContextService,
        officeHomeApiConfigService: OfficeHomeApiConfigService,
        private readonly oneNoteConfigService: OneNoteConfigService,
        platformLocation: PlatformLocation,
        scheduler: SchedulerService
    ) {
        super(accessTokenService, instrumentationService, instrumentedHttp, AccessTokenConsumer.OneNote, contextService, officeHomeApiConfigService, platformLocation, scheduler);
        this.inMockMode = contextService.sharedContext.inMockMode;
    }

    public getClassNoteBooks(): Promise<Array<ClassNoteBook>> {
        const requestConfig: HttpRequestOptions = {};
        if (this.getClassNoteBookPromise) {
            return this.getClassNoteBookPromise;
        }
        this.getClassNoteBookPromise = new Promise<Array<ClassNoteBook>>((resolve, reject) => {
            this.get<OneNoteGetClassNoteBookResponse>(KnownInstrumentationEvents.GetClassNotebooksRequest, this.oneNoteConfigService.config.oneNoteApiPath + this.BASE_URL + '/v' + this.VERSION + '/me/notes/classnotebooks?hasaccessto=true&orderby=lastModifiedTime desc', (promise: InstrumentedHttpPromise<OneNoteGetClassNoteBookResponse>) => {
                promise.instrument((result, event) => {
                    event.params[InstrumentationParam.RequestId] = this.getRequestIdFromResponse(result);
                }).then(response => {
                    resolve(response.data.value);
                });
            }, error => {
                this.getClassNoteBookPromise = null;
                reject(error);
            }, requestConfig, FetchStrategy.NetworkOnly);
        });
        return this.getClassNoteBookPromise;
    }

    public generateSaveCopyClassNotebookLink(notebookId: string, notebookName: string, notebookUrl: string) {
        const resourceUrl = this.inMockMode ? this.CLIENT_DEV_MODE_RESOURCE_URL : this.oneNoteConfigService.config.oneNoteApiPath;
        const dashIdx = notebookId.indexOf('-');
        const firstDigit = notebookId.substring(0, dashIdx);
        const identifier = notebookId.substring(dashIdx + 1).toLowerCase();
        return `${resourceUrl}${this.COPY_CLASS_NOTEBOOK}/?notebookId=${firstDigit}|{${identifier}}&notebookName=${notebookName}&notebookUrl=${notebookUrl}`;
    }

    public getNotebookSections(siteId: string, webId: string, listItemUniqueId: string): Promise<Array<OneNoteSection>> {
        const endpoint = `/myOrganization/siteCollections/${siteId}/sites/${webId}/notes/notebooks/1-${listItemUniqueId}/sections`;
        const requestConfig: HttpRequestOptions = {};
        return new Promise<Array<OneNoteSection>>((resolve, reject) => {
            this.get<OneNoteGetNoteBookSectionsResponse>(
                KnownInstrumentationEvents.GetOneNoteSectionsRequest,
                this.oneNoteConfigService.config.oneNoteApiPath + this.BASE_URL + '/v' + this.VERSION + endpoint,
                (promise: InstrumentedHttpPromise<OneNoteGetNoteBookSectionsResponse>) => {
                    promise.instrument((result, event) => {
                        event.params[InstrumentationParam.RequestId] = this.getRequestIdFromResponse(result);
                    }).then(response => {
                        const sectionItem = (response && response.data && response.data.value || []).map(section =>
                            ({
                                name: section.name,
                                siteId,
                                webId,
                                listItemUniqueId,
                                sectionId: section.id,
                                key: section.id
                            }));

                        resolve(sectionItem);
                    });
                },
                error => { reject(error); }, requestConfig, FetchStrategy.NetworkOnly
            );
        });
    }

    public createPageInSection(siteId: string, webId: string, sectionId: string, pageName: string): Promise<string> {
        const requestConfig: HttpRequestOptions = {
            headers: new Map()
        };
        requestConfig.headers.set('Content-Type', 'text/html');
        const endpoint = `/myOrganization/siteCollections/${siteId}/sites/${webId}/notes/sections/${sectionId}/pages`;
        return new Promise<string>((resolve, reject) => {
            this.post<GraphOneNotePage>(
                KnownInstrumentationEvents.GetOneNotePageRequest,
                this.oneNoteConfigService.config.oneNoteApiPath + this.BASE_URL + '/v' + this.VERSION + endpoint,
                this.newPageHtml.replace('{PAGETITLE}', pageName),
                (promise: InstrumentedHttpPromise<GraphOneNotePage>) => {
                    promise
                        .instrument((result, event) => {
                            event.params[InstrumentationParam.RequestId] = this.getRequestIdFromResponse(result);
                        })
                        .then(response => {
                            const item = response.data;
                            if (item && item.links && item.links.oneNoteWebUrl.href) {
                                resolve(item.links.oneNoteWebUrl.href);
                            } else {
                                reject('error parsing response');
                            }
                        })
                        .catch(error => {
                            reject(error);
                        });
                }, (xhrError: XhrServiceError<GraphOneNotePage>) => {
                    if (xhrError && xhrError.httpResponse) {
                        if (xhrError.httpResponse.data) {
                            reject({ error: { code: `${xhrError.httpResponse.status}`, data: `${xhrError.httpResponse.data}` } });
                        } else {
                            reject({ error: { code: `${xhrError.httpResponse.status}` } });
                        }
                    } else {
                        reject();
                    }
                }, requestConfig
            );
        });
    }

    protected getRequestIdFromResponse(response: InstrumentedHttpDataResponse<any>): string {
        return response.headers.get('X-CorrelationId');
    }
}
